import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LogOut, Save, Package, Repeat } from "lucide-react";

export default function AccountPage() {
  const { user, isCustomer, updateProfile, logout } = useAuth();
  const [profile, setProfile] = useState({ name: "", phone: "", shipping_address: "" });
  const [orders, setOrders] = useState([]);
  const [trades, setTrades] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  /* eslint-disable */
  useEffect(() => {
    if (isCustomer) {
      setProfile({
        name: user.name || "",
        phone: user.phone || "",
        shipping_address: user.shipping_address || "",
      });
      api.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/customer/trades").then((r) => setTrades(r.data)).catch(() => {});
    }
  }, [isCustomer, user]);
  /* eslint-enable */

  if (user === null) {
    return <div className="text-center py-32 text-slate-400" data-testid="account-loading">Checking session…</div>;
  }
  if (!isCustomer) {
    return <Navigate to="/signin" replace />;
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await updateProfile(profile);
    setSaving(false);
    if (res.ok) setSavedAt(Date.now());
  };

  const statusColor = {
    new: "bg-blue-500/20 text-blue-300",
    contacted: "bg-yellow-400/20 text-yellow-300",
    queued: "bg-purple-500/20 text-purple-300",
    completed: "bg-emerald-500/20 text-emerald-300",
    cancelled: "bg-red-500/20 text-red-300",
    reviewing: "bg-yellow-400/20 text-yellow-300",
    matched: "bg-emerald-500/20 text-emerald-300",
    closed: "bg-slate-500/20 text-slate-300",
  };

  return (
    <div data-testid="account-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">// Your account</div>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl font-black tracking-tighter">Hey, {user.name}.</h1>
          <p className="mt-2 text-slate-400 text-sm">{user.email}</p>
        </div>
        <button data-testid="account-logout" onClick={logout} className="bg-slate-900 border border-slate-700 text-slate-300 hover:text-white px-4 py-2 rounded-md text-sm">
          <LogOut className="w-4 h-4 inline mr-1" /> Sign out
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Saved profile */}
        <form onSubmit={save} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <h2 className="font-bold text-white text-lg">Saved info</h2>
          <p className="text-xs text-slate-500">Auto-filled on every future order & trade request.</p>
          <Field label="Name">
            <input data-testid="account-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={input} />
          </Field>
          <Field label="Phone">
            <input data-testid="account-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={input} />
          </Field>
          <Field label="Default shipping address">
            <textarea data-testid="account-address" rows={3} value={profile.shipping_address} onChange={(e) => setProfile({ ...profile, shipping_address: e.target.value })} className={input} />
          </Field>
          <div className="flex items-center gap-3">
            <button data-testid="account-save" type="submit" disabled={saving} className="bg-yellow-400 text-slate-950 font-bold px-4 py-2 rounded-md uppercase text-sm hover:bg-yellow-300 disabled:opacity-50">
              <Save className="w-4 h-4 inline mr-1" /> {saving ? "Saving…" : "Save"}
            </button>
            {savedAt && <span className="text-xs text-emerald-400 font-mono">Saved ✓</span>}
          </div>
        </form>

        {/* Stats card */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h2 className="font-bold text-white text-lg">Activity</h2>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-4">
              <Package className="w-5 h-5 text-yellow-400" />
              <div className="font-mono text-3xl font-black text-white mt-2">{orders.length}</div>
              <div className="text-xs text-slate-500 font-mono uppercase">Orders placed</div>
            </div>
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-4">
              <Repeat className="w-5 h-5 text-yellow-400" />
              <div className="font-mono text-3xl font-black text-white mt-2">{trades.length}</div>
              <div className="text-xs text-slate-500 font-mono uppercase">Trade requests</div>
            </div>
          </div>
        </div>
      </div>

      {/* Order history */}
      <div className="mt-10 bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="font-bold text-white text-lg">Order history</h2>
        </div>
        {orders.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No orders yet — head to the shop!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-950">
                <tr>
                  <Th>Date</Th><Th>Product</Th><Th>Type</Th><Th>Total</Th><Th>Status</Th><Th />
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const itemList = (o.items && o.items.length > 0)
                    ? o.items
                    : [{ product_name: o.product_name, quantity: o.quantity }];
                  return (
                  <tr key={o.id} className="border-t border-slate-800/50">
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{o.created_at.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-white">
                      {itemList.length === 1 ? (
                        <>{itemList[0].product_name} <span className="text-slate-500">×{itemList[0].quantity}</span></>
                      ) : (
                        <div className="space-y-0.5">
                          {itemList.map((it, i) => (
                            <div key={i} className="text-xs">{it.product_name} <span className="text-slate-500">×{it.quantity}</span></div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{o.fulfillment === "rip" ? "Rip" : "Sealed"}</td>
                    <td className="px-4 py-3 font-mono text-yellow-400 font-bold tabular-nums">
                      {o.total > 0 ? `$${o.total.toFixed(2)}` : <span className="text-slate-500 font-normal">Pending</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded ${statusColor[o.status] || ""}`}>{o.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <a href={`/order-status/${o.id}`} className="text-yellow-400 hover:text-yellow-300 text-[10px] uppercase font-mono">View →</a>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Trade history */}
      <div className="mt-8 bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="font-bold text-white text-lg">Your trade requests</h2>
        </div>
        {trades.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No trades submitted yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-950">
                <tr><Th>Date</Th><Th>Card</Th><Th>Condition</Th><Th>Want</Th><Th>Status</Th></tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <tr key={t.id} className="border-t border-slate-800/50">
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{t.created_at.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-white">{t.card_name} <span className="text-slate-500">{t.set_name}</span></td>
                    <td className="px-4 py-3 text-slate-400">{t.condition}</td>
                    <td className="px-4 py-3 text-slate-400 truncate max-w-xs">{t.looking_for}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded ${statusColor[t.status] || ""}`}>{t.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const input = "w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-yellow-400";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Th({ children }) {
  return <th className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-slate-500">{children}</th>;
}
